import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { RootState } from '@store/index';
import type { Patch } from 'immer';

import {
  clearHistory,
  finishRedo,
  finishUndo,
  performRedo,
  performUndo,
  pushUndoAction,
  type UndoAction,
} from '../store/templateUndoSlice';
import type { NoteSection } from '../types';

/**
 * Hook options for template undo
 */
export interface UseTemplateUndoOptions {
  /** Note ID to track undo history for */
  noteId?: string;
  /** Enable keyboard shortcuts (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z) */
  enableKeyboardShortcuts?: boolean;
  /** Callback when undo is triggered */
  onUndo?: (action: UndoAction) => void | Promise<void>;
  /** Callback when redo is triggered */
  onRedo?: (action: UndoAction) => void | Promise<void>;
}

/**
 * Hook return value
 */
export interface UseTemplateUndoReturn {
  /** Can perform undo */
  canUndo: boolean;
  /** Can perform redo */
  canRedo: boolean;
  /** Last action that can be undone */
  lastAction?: UndoAction;
  /** Next action that can be redone */
  nextAction?: UndoAction;
  /** Number of actions in undo stack */
  undoCount: number;
  /** Number of actions in redo stack */
  redoCount: number;
  /** Record a new undoable action */
  recordAction: (params: {
    type: 'add' | 'remove' | 'reorder';
    description: string;
    patches: Patch[];
    inversePatches: Patch[];
    sectionOrder?: string[];
    previousSectionOrder?: string[];
    deletedSection?: NoteSection;
    deletedSectionIndex?: number;
    addedModuleIds?: string[];
    addedSectionIndex?: number;
  }) => void;
  /** Perform undo */
  undo: () => Promise<void>;
  /** Perform redo */
  redo: () => Promise<void>;
  /** Clear all history */
  clear: () => void;
}

/**
 * Template Undo Hook
 *
 * Provides undo/redo functionality for template editing.
 * Integrates with RTK Query cache updates via Immer patches.
 *
 * @example
 * ```tsx
 * const { canUndo, canRedo, undo, redo, recordAction } = useTemplateUndo({
 *   noteId,
 *   enableKeyboardShortcuts: true,
 *   onUndo: async (action) => {
 *     // Apply inverse patches and call API
 *   }
 * })
 * ```
 */
export function useTemplateUndo(options: UseTemplateUndoOptions = {}): UseTemplateUndoReturn {
  const { noteId, enableKeyboardShortcuts = true, onUndo, onRedo } = options;

  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { past, future, isUndoing, isRedoing } = useSelector((state: RootState) => {
    // Access the templateUndo feature state - will be undefined until feature is loaded
    if (!state.templateUndo) {
      return { past: [], future: [], isUndoing: false, isRedoing: false };
    }
    return state.templateUndo;
  });

  // Filter actions for current note (if noteId provided)
  const filteredPast = noteId ? past.filter((action: UndoAction) => action.noteId === noteId) : past;
  const filteredFuture = noteId ? future.filter((action: UndoAction) => action.noteId === noteId) : future;

  const canUndo = filteredPast.length > 0 && !isUndoing;
  const canRedo = filteredFuture.length > 0 && !isRedoing;
  const lastAction = filteredPast[filteredPast.length - 1];
  const nextAction = filteredFuture[0];

  /**
   * Record a new undoable action
   */
  const recordAction = useCallback(
    (params: {
      type: 'add' | 'remove' | 'reorder';
      description: string;
      patches: Patch[];
      inversePatches: Patch[];
      sectionOrder?: string[];
      previousSectionOrder?: string[];
      deletedSection?: NoteSection;
      deletedSectionIndex?: number;
      addedModuleIds?: string[];
      addedSectionIndex?: number;
    }) => {
      if (!noteId) {
        console.warn('Cannot record undo action without noteId');
        return;
      }

      const action: UndoAction = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: params.type,
        noteId,
        description: params.description,
        timestamp: Date.now(),
        patches: params.patches,
        inversePatches: params.inversePatches,
        sectionOrder: params.sectionOrder,
        previousSectionOrder: params.previousSectionOrder,
        deletedSection: params.deletedSection,
        deletedSectionIndex: params.deletedSectionIndex,
        addedModuleIds: params.addedModuleIds,
        addedSectionIndex: params.addedSectionIndex,
      };

      dispatch(pushUndoAction(action));
    },
    [noteId, dispatch]
  );

  /**
   * Perform undo operation
   */
  const undo = useCallback(async () => {
    // Read fresh state from store to avoid stale closure issues
    const state = store.getState();
    const undoState = state.templateUndo;

    if (!undoState) return;

    // Filter actions for current note
    const currentPast = noteId
      ? undoState.past.filter((action: UndoAction) => action.noteId === noteId)
      : undoState.past;

    // Check if we can undo
    if (currentPast.length === 0 || undoState.isUndoing) return;

    // Get the action to undo from fresh state
    const actionToUndo = currentPast[currentPast.length - 1];

    dispatch(performUndo());

    try {
      if (onUndo && actionToUndo) {
        await onUndo(actionToUndo);
      }
    } finally {
      dispatch(finishUndo());
    }
  }, [noteId, onUndo, dispatch, store]);

  /**
   * Perform redo operation
   */
  const redo = useCallback(async () => {
    // Read fresh state from store to avoid stale closure issues
    const state = store.getState();
    const undoState = state.templateUndo;

    if (!undoState) return;

    // Filter actions for current note
    const currentFuture = noteId
      ? undoState.future.filter((action: UndoAction) => action.noteId === noteId)
      : undoState.future;

    // Check if we can redo
    if (currentFuture.length === 0 || undoState.isRedoing) return;

    // Get the action to redo from fresh state
    const actionToRedo = currentFuture[0];

    dispatch(performRedo());

    try {
      if (onRedo && actionToRedo) {
        await onRedo(actionToRedo);
      }
    } finally {
      dispatch(finishRedo());
    }
  }, [noteId, onRedo, dispatch, store]);

  /**
   * Clear history
   */
  const clear = useCallback(() => {
    dispatch(clearHistory());
  }, [dispatch]);

  /**
   * Keyboard shortcut handler
   */
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        void undo();
      }
      // Cmd+Shift+Z (Mac) or Ctrl+Shift+Z (Windows/Linux)
      else if ((event.metaKey || event.ctrlKey) && event.key === 'z' && event.shiftKey) {
        event.preventDefault();
        void redo();
      }
      // Cmd+Y (Windows alternative for redo)
      else if ((event.metaKey || event.ctrlKey) && event.key === 'y') {
        event.preventDefault();
        void redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardShortcuts, undo, redo]);

  /**
   * Clear history when noteId changes
   */
  useEffect(() => {
    if (noteId) {
      // Clear history for different notes when switching
      return () => {
        // Optionally clear on unmount
        // dispatch(clearHistoryForNote(noteId))
      };
    }
  }, [noteId]);

  return {
    canUndo,
    canRedo,
    lastAction,
    nextAction,
    undoCount: filteredPast.length,
    redoCount: filteredFuture.length,
    recordAction,
    undo,
    redo,
    clear,
  };
}
